import { Link } from "react-router-dom";
import { ArrowRight, Compass, Building2, Trophy } from "lucide-react";
import { useFetch } from "@/lib/useFetch";
import { Seo } from "@/lib/seo";
import { Loading } from "@/components/States";
import { VenueCard, EventCard, CityCard } from "@/components/Cards";
import { CTAButton } from "@/components/CTAButton";
import { SearchBar } from "@/components/SearchBar";
import { NewsletterSignup } from "@/components/NewsletterSignup";

import { onImgError } from "@/lib/images";
const HERO = "https://images.unsplash.com/photo-1782130056357-3339bb395a7a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

function Section({ eyebrow, title, children, action, className = "" }) {
  return (
    <section className={`mx-auto max-w-7xl px-5 sm:px-8 py-16 ${className}`}>
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          {eyebrow && <div className="overline text-primary mb-2">{eyebrow}</div>}
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-ink">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function HomePage() {
  const { data, loading } = useFetch("/home", []);

  return (
    <>
      <Seo
        title="Hotelstrips — Travel for the event. Stay for the experience."
        description="Hotelstrips helps travelers decide where to stay, how to get there, and what works best around the game, concert or event they're attending. Start with GameDay."
        image={HERO}
        path="/"
        jsonLd={{ "@context": "https://schema.org", "@type": "WebSite", name: "Hotelstrips", url: window.location.origin, potentialAction: { "@type": "SearchAction", target: `${window.location.origin}/gameday?q={query}`, "query-input": "required name=query" } }}
      />

      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-center">
        <img src={HERO} alt="Fans at a stadium" onError={onImgError} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full">
          <div className="max-w-2xl animate-fade-up">
            <div className="overline text-primary mb-4">Hotelstrips GameDay</div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-white leading-[0.95]">
              Travel for the event.<br /><span className="text-primary">Stay</span> for the experience.
            </h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-xl">
              Where should you stay for the game, the concert, the tournament? We map the best
              areas, the smartest transit and the right hotels — before you ever hit a booking site.
            </p>

            {/* Discovery search */}
            <div className="mt-8 max-w-xl">
              <SearchBar />
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {[["MetLife Stadium", "/venues/metlife-stadium"], ["Yankee Stadium", "/venues/yankee-stadium"], ["US Open", "/events/us-open-2026"], ["Citi Field", "/venues/citi-field"]].map(([l, to]) => (
                <Link key={to} to={to} className="rounded-full border border-white/25 px-4 py-1.5 text-xs font-semibold text-white/90 hover:bg-white hover:text-ink transition-colors">
                  {l}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 -mt-10 relative z-10">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { icon: Compass, t: "Decide where to stay", d: "Ranked stay areas with pros, cons and the real reason each one works." },
            { icon: Building2, t: "See how to get there", d: "Transit, driving, airport and gameday logistics for every venue." },
            { icon: Trophy, t: "Then book with confidence", d: "Jump to hotels and flights once you know the plan." },
          ].map((v) => (
            <div key={v.t} className="rounded-xl bg-card border border-black/5 card-shadow p-6">
              <span className="grid h-11 w-11 place-items-center rounded-lg bg-primary/10 text-primary"><v.icon className="h-5 w-5" /></span>
              <h3 className="font-heading text-lg font-bold text-ink mt-4">{v.t}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {loading ? <Loading /> : (
        <>
          <Section eyebrow="Featured GameDay destinations" title="Where should I stay?"
            action={<Link to="/gameday" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all">Browse all <ArrowRight className="h-4 w-4" /></Link>}>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data?.featured_venues?.map((v) => <VenueCard key={v.slug} venue={v} />)}
            </div>
          </Section>

          <div className="bg-muted/40 border-y border-black/5">
            <Section eyebrow="Popular upcoming events" title="Plan around the calendar">
              <div className="grid gap-4 md:grid-cols-2">
                {data?.featured_events?.map((e) => <EventCard key={e.slug} event={e} />)}
              </div>
            </Section>
          </div>

          <Section eyebrow="Stay area intelligence" title="Not just hotels — the right neighborhood">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {data?.featured_stay_areas?.map((a) => (
                <Link key={a.slug} to={`/venues/${a.venue_rankings?.[0]?.venue_slug || "metlife-stadium"}`} data-testid={`home-area-${a.slug}`} className="group relative overflow-hidden rounded-xl min-h-[260px] block transition-transform duration-300 hover:-translate-y-1">
                  <img src={a.image_url} alt={a.image_alt || a.name} loading="lazy" onError={onImgError} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 img-overlay" />
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <span className="inline-block rounded-full bg-primary px-2.5 py-1 text-[10px] font-heading font-bold text-primary-foreground mb-2">{a.best_for}</span>
                    <h3 className="font-heading text-xl font-bold text-white drop-shadow">{a.name}</h3>
                    <p className="text-xs text-white/70 mt-1 line-clamp-2">{a.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
          </Section>

          <Section eyebrow="City guides" title="Cities built for a sports weekend">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data?.featured_cities?.map((c) => <CityCard key={c.slug} city={c} />)}
            </div>
          </Section>

          {/* Commerce CTA band */}
          <section className="mx-auto max-w-7xl px-5 sm:px-8 py-8">
            <div className="rounded-2xl bg-ink text-white p-8 sm:p-12 overflow-hidden relative">
              <div className="relative z-10 max-w-xl">
                <div className="overline text-primary mb-3">Ready when you are</div>
                <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter">Know the plan. Then book it.</h2>
                <p className="text-white/70 mt-3">Hotels and flights — once Hotelstrips helps you decide where to stay.</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <CTAButton productType="hotels" ctx={{ page: "home", position: "home_band" }} />
                  <CTAButton productType="flights" variant="outline" className="!text-white !border-white/30" ctx={{ page: "home", position: "home_band" }} />
                </div>
              </div>
            </div>
          </section>

          <Section eyebrow="Travel inspiration" title="Editorial guides">
            <div className="grid gap-5 md:grid-cols-3">
              {data?.guides?.map((g) => (
                <Link key={g.slug} to={`/guides/${g.slug}`} data-testid={`guide-card-${g.slug}`} className="group rounded-xl overflow-hidden bg-card border border-black/5 card-shadow transition-transform duration-300 hover:-translate-y-1">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={g.hero_image} alt={g.title} loading="lazy" onError={onImgError} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <div className="overline text-primary mb-2">{g.category}</div>
                    <h3 className="font-heading text-lg font-bold text-ink leading-tight">{g.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </Section>

          <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-8">
            <div className="rounded-2xl border border-black/5 bg-card card-shadow p-8 sm:p-12 flex flex-col items-center text-center">
              <div className="overline text-primary mb-3">Never miss a matchup</div>
              <h2 className="font-heading text-3xl font-extrabold tracking-tighter text-ink max-w-lg">Get GameDay travel picks in your inbox</h2>
              <p className="text-muted-foreground mt-2 max-w-md">Where to stay for the biggest games and events, before prices move.</p>
              <div className="mt-6"><NewsletterSignup /></div>
            </div>
          </section>
        </>
      )}
    </>
  );
}
